import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/** Standard Laravel API response body shape */
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message?: string;
  errors?: unknown;
}

export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  current_items?: number;
  from?: number;
  to?: number;
  total: number;
}

export interface PaginatedListData<T> {
  items: T[];
}

export interface PaginatedApiResponse<T> extends ApiResponse<PaginatedListData<T> | T[]> {
  paging?: PaginationInfo;
}

export type ListPayload<T> = PaginatedListData<T> | T[];

/** Extract list items from an API `data` field (plain array or `{ items: T[] }`). */
export function extractListItems<T>(data: unknown): T[] {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data)) {
    return data as T[];
  }
  if (typeof data === 'object' && 'items' in data) {
    const items = (data as PaginatedListData<T>).items;
    return Array.isArray(items) ? items : [];
  }
  return [];
}

/** Extract paging nested on a paginated data object. */
export function extractPaging(data: unknown): PaginationInfo | undefined {
  if (data != null && typeof data === 'object' && !Array.isArray(data) && 'paging' in data) {
    return (data as { paging?: PaginationInfo }).paging;
  }
  return undefined;
}

/** Extract paging from a paginated API response body or nested data object. */
export function extractResponsePaging(response: AxiosResponse<unknown>): PaginationInfo | undefined {
  const body = response.data as { paging?: PaginationInfo; data?: unknown };
  if (body.paging) {
    return body.paging;
  }
  return extractPaging(body.data);
}

/** Normalize partial paging metadata with sensible defaults. */
export function normalizePaging(
  raw: Partial<PaginationInfo> | undefined,
  itemCount: number,
  defaults: Partial<PaginationInfo> = {}
): PaginationInfo {
  return {
    current_page: raw?.current_page ?? defaults.current_page ?? 1,
    last_page: raw?.last_page ?? defaults.last_page ?? 1,
    per_page: raw?.per_page ?? defaults.per_page ?? 10,
    total: raw?.total ?? itemCount,
    from: raw?.from ?? defaults.from ?? 0,
    to: raw?.to ?? defaults.to ?? 0,
    current_items: raw?.current_items ?? itemCount,
  };
}

/** Service-layer result with HTTP status (distinct from ApiResponse body shape). */
export interface ApiServiceResult<T> {
  data: T;
  status: number;
  message: string;
}

// Create and configure the axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1/',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for handling common response patterns
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  (error) => {
    // Handle authentication errors
    if (error.response && error.response.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper methods for API calls — return AxiosResponse; body is in `.data`
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.get<T>(url, config),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.post<T>(url, data, config),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.put<T>(url, data, config),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.patch<T>(url, data, config),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.delete<T>(url, config),
};

export default apiClient; 